/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITag, ITagItemProps, ITagItemStyles, TagItem } from '@fluentui/react/lib/Pickers';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { getScopedTheme } from 'src/core/themes/luisStylist';

const tagsTheme = getScopedTheme('PhraseListTagsRenderer');

const addIconStyles = classNamesFunction<ITagItemProps, ITagItemStyles>()({
    close: { selectors: { i: { fontSize: tagsTheme.iconFontSize, transform: tagsTheme.iconRotation } } }
});

/**
 * A unified function to convert values string array to
 * a string. This is to unify the joining experience if
 * it were to change in the future for any reason.
 *
 * @param values The values string array to convert to string.
 * @returns A string representation of the values array.
 */
export const stringArrayToString = (values: string[]): string => values.join(',');

/**
 * A unified function to convert values string to
 * a string array. This is to unify the splitting
 * experience if it were to change in the future
 * for any reason.
 *
 * @param values The values string to convert to
 * string array.
 * @returns A string array representation of the
 * string values.
 */
export const stringToStringArray = (values: string): string[] => values
        .split(',')
        .map(t => t.toLocaleLowerCase().trim())
        .filter(t => t.length);

/**
 * Gets the tag data from the given value of comma separated
 * phrase list phrases. Values are unique and thus are suitable
 * to be used as a key for the tag.
 *
 * @param values The string of comma separated values to
 * return as tag data.
 * @returns An array of tag data items.
 */
export const stringArrayToTags = (values: string[]): ITag[] => {
    if (!values) {
        return [];
    }

    return values.map(value => ({ key: value, name: value }));
};

/**
 * Converts tag data items to tag items. A tag item is selected
 * if its key exists in the array of selected items.
 *
 * @param data The tag data to convert to tag items.
 * @param selectedData The selected tag data that will be used
 * to render tag data as either selected or not.
 * @param icon Whether the tag icon is for adding or deleting
 * a tag.
 * @returns An array of tag item components.
 */
export const renderTags = (
    items: ITag[],
    selectedItems: ITag[],
    onRemoveItems: (values: string[]) => void,
    icon: 'add' | 'remove',
    removeButtonAriaLabel: string,
    areTagsDisbaled?: boolean
) => {
    const isSelected = (tag: ITag): boolean => selectedItems.some(selectedItem => selectedItem.key === tag.key);

    return items.map((tag: ITag, index: number) => (
        <TagItem
            item={tag}
            index={index}
            key={tag.key}
            selected={isSelected(tag)}
            removeButtonAriaLabel={removeButtonAriaLabel}
            styles={icon === 'add' ? addIconStyles : null}
            onRemoveItem={() => onRemoveItems && onRemoveItems([tag.name])}
            disabled={areTagsDisbaled}>
            {tag.name}
        </TagItem>
    ));
};
